import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, LogOut, ExternalLink, ChevronDown, ChevronRight, Upload } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ORDER_STATUSES = ["awaiting_payment", "paid", "shipped", "delivered", "cancelled"];
const AUDIENCE_LABELS = { customers: "Customers only", subscribers: "Newsletter subscribers only", both: "Everyone (customers + subscribers)" };
const orderCodes = (o) => (o.discount_codes ? o.discount_codes.map((c) => c.code) : (o.discount_code ? [o.discount_code] : []));
const EMPTY_PRODUCT = {
  name: "", tagline: "", description: "", price: "", category: "", image: "",
  stock: 100, ingredients: "", benefits: "", featured: false, in_stock: true,
};

function StatCard({ label, value, testid }) {
  return (
    <div className="border-r border-b border-border p-8" data-testid={testid}>
      <p className="label text-muted-foreground mb-3">{label}</p>
      <p className="font-display text-5xl font-light">{value}</p>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [broadcast, setBroadcast] = useState({ subject: "", message: "", audience: "both" });
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [openBroadcast, setOpenBroadcast] = useState(null);
  const [broadcastDetail, setBroadcastDetail] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [newDiscount, setNewDiscount] = useState({ code: "", percent_off: 10, type: "percent", expires_at: "", amount: "" });
  const [threshold, setThreshold] = useState(5);
  const [thresholdInput, setThresholdInput] = useState("5");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [restockCounts, setRestockCounts] = useState({});

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/discounts").then(({ data }) => setDiscounts(data)).catch(() => {});
    api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/admin/subscribers").then(({ data }) => setSubscribers(data)).catch(() => {});
    api.get("/affiliate").then(({ data }) => setAffiliates(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => { setThreshold(data.low_stock_threshold); setThresholdInput(String(data.low_stock_threshold)); }).catch(() => {});
    api.get("/admin/restock-counts").then(({ data }) => setRestockCounts(data)).catch(() => {});
    api.get("/admin/broadcasts").then(({ data }) => setBroadcasts(data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live-refresh history while any broadcast is still running
  useEffect(() => {
    if (!broadcasts.some((b) => b.status === "running")) return;
    const t = setTimeout(() => {
      api.get("/admin/broadcasts").then(({ data }) => setBroadcasts(data)).catch(() => {});
      if (openBroadcast) {
        api.get(`/admin/broadcasts/${openBroadcast}`).then(({ data }) => setBroadcastDetail(data)).catch(() => {});
      }
    }, 4000);
    return () => clearTimeout(t);
  }, [broadcasts, openBroadcast]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const openNew = () => { setEditing(null); setForm(EMPTY_PRODUCT); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p, price: String(p.price),
      ingredients: (p.ingredients || []).join(", "),
      benefits: (p.benefits || []).join(", "),
    });
    setDialogOpen(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock) || 0,
      ingredients: form.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
      benefits: form.benefits.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteProduct = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Product deleted"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleAvailability = async (p) => {
    try {
      const { data } = await api.patch(`/products/${p.id}/availability`, { in_stock: !p.in_stock });
      const note = data.notified ? ` — restock email sent to ${data.notified} shopper${data.notified === 1 ? "" : "s"}` : "";
      toast.success(`${p.name} marked ${p.in_stock ? "sold out" : "available"}${note}`);
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveThreshold = async () => {
    const val = parseInt(thresholdInput);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid number"); setThresholdInput(String(threshold)); return; }
    try {
      await api.put("/admin/settings", { low_stock_threshold: val });
      setThreshold(val);
      toast.success(`Low stock alerts trigger below ${val} units`);
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingImage(true);
    try {
      const { data } = await api.post("/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const changeStatus = async (id, status) => {
    let payload = { status };
    if (status === "shipped") {
      const tn = window.prompt("Enter tracking number (optional — leave blank to send without one):", "");
      if (tn === null) return; // cancelled prompt
      if (tn.trim()) payload.tracking_number = tn.trim();
    }
    if (status === "cancelled" && !window.confirm("Cancel this order and email the customer?")) return;
    try { await api.patch(`/orders/${id}/status`, payload); toast.success(`Status updated → ${status.replace(/_/g, " ")}`); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const createDiscount = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: newDiscount.code,
        type: newDiscount.type,
        percent_off: newDiscount.type === "percent" ? parseInt(newDiscount.percent_off) : 0,
        active: true,
        expires_at: newDiscount.expires_at || null,
        amount: newDiscount.type === "fixed" ? parseFloat(newDiscount.amount) : null,
        max_uses: null,
      };
      await api.post("/discounts", payload);
      toast.success("Discount created");
      setNewDiscount({ code: "", percent_off: 10, type: newDiscount.type });
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteDiscount = async (id) => {
    try { await api.delete(`/discounts/${id}`); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const approveAffiliate = async (id) => {
    if (!window.confirm("Approve this affiliate and generate a unique promo code?")) return;
    try {
      const { data } = await api.post(`/affiliate/${id}/approve`);
      toast.success(`Approved — code ${data.code}`);
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [expandedAffiliate, setExpandedAffiliate] = useState(null);
  const [affiliateOrders, setAffiliateOrders] = useState({});
  const toggleAffiliateOrders = async (id) => {
    if (expandedAffiliate === id) { setExpandedAffiliate(null); return; }
    setExpandedAffiliate(id);
    if (!affiliateOrders[id]) {
      try {
        const { data } = await api.get(`/affiliate/${id}/orders`);
        setAffiliateOrders((m) => ({ ...m, [id]: data }));
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    }
  };

  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcast.subject.trim() || !broadcast.message.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    if (!window.confirm(`Send this email to ${AUDIENCE_LABELS[broadcast.audience].toLowerCase()}?`)) return;
    setBroadcasting(true);
    try {
      const { data } = await api.post("/admin/broadcast", broadcast);
      toast.success(`Broadcast started — sending to ${data.total} recipients in the background. Track it in the history below.`);
      setBroadcast({ subject: "", message: "", audience: broadcast.audience });
      api.get("/admin/broadcasts").then(({ data: jobs }) => setBroadcasts(jobs)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBroadcasting(false);
    }
  };

  const toggleBroadcastDetail = async (id) => {
    if (openBroadcast === id) { setOpenBroadcast(null); setBroadcastDetail(null); return; }
    setOpenBroadcast(id);
    setBroadcastDetail(null);
    try {
      const { data } = await api.get(`/admin/broadcasts/${id}`);
      setBroadcastDetail(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "products", label: "Products" },
    { id: "orders", label: "Orders" },
    { id: "customers", label: "Customers" },
    { id: "subscribers", label: "Subscribers" },
    { id: "affiliates", label: "Affiliates" },
    { id: "broadcast", label: "Broadcast" },
    { id: "discounts", label: "Discounts" },
  ];
  const inputCls = "w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-accent transition-colors";

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      {/* Top bar */}
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="flex items-center justify-between px-6 md:px-10 h-16">
          <div className="flex items-center gap-4">
            <span className="font-display text-xl">Coastal Chemistry</span>
            <span className="label text-muted-foreground border-l border-border pl-4">Admin Console</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="label text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1" data-testid="admin-view-store">
              View store <ExternalLink size={14} strokeWidth={1.5} />
            </Link>
            <span className="label text-muted-foreground hidden sm:inline">{user?.email}</span>
            <button onClick={handleLogout} className="text-muted-foreground hover:text-destructive transition-colors" data-testid="admin-logout"><LogOut size={18} strokeWidth={1.5} /></button>
          </div>
        </div>
        <nav className="flex px-6 md:px-10 border-t border-border">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`label px-4 py-3 border-b-2 transition-colors ${tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="px-6 md:px-10 py-10">
        {/* OVERVIEW */}
        {tab === "overview" && stats && (
          <div className="border-t border-l border-border grid grid-cols-2 lg:grid-cols-3" data-testid="admin-overview">
            <StatCard label="Revenue" value={`$${stats.revenue.toFixed(0)}`} testid="stat-revenue" />
            <StatCard label="Orders" value={stats.orders} testid="stat-orders" />
            <StatCard label="Pending" value={stats.pending_orders} testid="stat-pending" />
            <StatCard label="Products" value={stats.products} testid="stat-products" />
            <StatCard label="Customers" value={stats.customers} testid="stat-customers" />
            <StatCard label="Subscribers" value={stats.subscribers} testid="stat-subscribers" />
            {stats.low_stock != null && <StatCard label="Low stock" value={stats.low_stock} testid="stat-low-stock" />}
          </div>
        )}

        {/* PRODUCTS */}
        {tab === "products" && (
          <div data-testid="admin-products">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
              <h2 className="font-display text-3xl font-light">Products ({products.length})</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2" data-testid="low-stock-threshold-control">
                  <label htmlFor="low-stock-threshold" className="label text-muted-foreground">Low stock alert below</label>
                  <input
                    id="low-stock-threshold"
                    type="number"
                    min={0}
                    value={thresholdInput}
                    onChange={(e) => setThresholdInput(e.target.value)}
                    onBlur={saveThreshold}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    className={`${inputCls} w-20 text-center`}
                    data-testid="low-stock-threshold-input"
                  />
                </div>
                <button onClick={openNew} className="label inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 hover:bg-accent transition-colors" data-testid="admin-add-product">
                  <Plus size={16} strokeWidth={1.5} /> New product
                </button>
              </div>
            </div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="label text-muted-foreground text-left">
                    <th className="p-3 border-b border-border font-normal">Name</th>
                    <th className="p-3 border-b border-border font-normal">Category</th>
                    <th className="p-3 border-b border-border font-normal">Price</th>
                    <th className="p-3 border-b border-border font-normal">Stock</th>
                    <th className="p-3 border-b border-border font-normal">Featured</th>
                    <th className="p-3 border-b border-border font-normal">Availability</th>
                    <th className="p-3 border-b border-border font-normal text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="font-mono hover:bg-secondary/50 transition-colors" data-testid={`admin-product-row-${p.id}`}>
                      <td className="p-3 border-b border-border">{p.name}</td>
                      <td className="p-3 border-b border-border text-muted-foreground">{p.category}</td>
                      <td className="p-3 border-b border-border">${p.price.toFixed(2)}</td>
                      <td className="p-3 border-b border-border">
                        {p.stock}
                        {p.stock < threshold && (
                          <span className="ml-2 label px-1.5 py-0.5 border border-destructive/50 text-destructive" data-testid={`low-stock-${p.id}`}>Low</span>
                        )}
                      </td>
                      <td className="p-3 border-b border-border">{p.featured ? "Yes" : "—"}</td>
                      <td className="p-3 border-b border-border">
                        <button
                          onClick={() => toggleAvailability(p)}
                          title="Click to toggle availability"
                          className={`label px-2.5 py-1 border transition-colors ${p.in_stock ? "border-border text-foreground/80 hover:border-destructive hover:text-destructive" : "border-destructive/50 text-destructive hover:border-foreground hover:text-foreground"}`}
                          data-testid={`admin-toggle-stock-${p.id}`}
                        >
                          {p.in_stock ? "Available" : "Sold Out"}
                        </button>
                        {!p.in_stock && restockCounts[p.id] > 0 && (
                          <div className="mt-1.5 label text-muted-foreground" data-testid={`restock-count-${p.id}`}>
                            {restockCounts[p.id]} waiting
                          </div>
                        )}
                      </td>
                      <td className="p-3 border-b border-border">
                        <div className="flex justify-end gap-3">
                          <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid={`admin-edit-${p.id}`}><Edit3 size={16} strokeWidth={1.5} /></button>
                          <button onClick={() => deleteProduct(p.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`admin-delete-${p.id}`}><Trash2 size={16} strokeWidth={1.5} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ORDERS */}
        {tab === "orders" && (
          <div data-testid="admin-orders">
            <h2 className="font-display text-3xl font-light mb-6">Orders ({orders.length})</h2>
            {orders.length === 0 ? (
              <p className="label text-muted-foreground">No orders yet.</p>
            ) : (
              <div className="border border-border divide-y divide-border">
                {orders.map((o) => (
                  <div key={o.id} className="p-4 grid md:grid-cols-12 gap-4 items-center font-mono text-sm" data-testid={`admin-order-${o.id}`}>
                    <div className="md:col-span-2"><span className="text-muted-foreground">#</span>{o.id.slice(-6).toUpperCase()}</div>
                    <div className="md:col-span-3">
                      <div>{o.shipping.full_name}</div>
                      <div className="text-xs text-muted-foreground">{o.shipping.email}</div>
                    </div>
                    <div className="md:col-span-3 text-muted-foreground text-xs">
                      {o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}
                      {orderCodes(o).length > 0 && (
                        <div className="mt-1 text-accent" data-testid={`order-codes-${o.id}`}>
                          Codes: {orderCodes(o).join(" + ")}{o.discount_amount ? ` (−$${o.discount_amount.toFixed(2)})` : ""}
                        </div>
                      )}
                    </div>
                    <div className="md:col-span-1">${o.total.toFixed(2)}</div>
                    <div className="md:col-span-3">
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger className="rounded-none border-border font-mono text-xs uppercase tracking-widest h-9" data-testid={`admin-order-status-${o.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {ORDER_STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="rounded-none font-mono text-xs uppercase tracking-widest">{s.replace(/_/g, " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CUSTOMERS */}
        {tab === "customers" && (
          <div data-testid="admin-customers">
            <h2 className="font-display text-3xl font-light mb-6">Customers ({customers.length})</h2>
            {customers.length === 0 ? (
              <p className="label text-muted-foreground">No customers yet.</p>
            ) : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="label text-muted-foreground text-left">
                      <th className="p-3 border-b border-border font-normal">Name</th>
                      <th className="p-3 border-b border-border font-normal">Email</th>
                      <th className="p-3 border-b border-border font-normal">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.id} className="font-mono hover:bg-secondary/50 transition-colors" data-testid={`admin-customer-${c.id}`}>
                        <td className="p-3 border-b border-border">{c.name}</td>
                        <td className="p-3 border-b border-border">{c.email}</td>
                        <td className="p-3 border-b border-border text-muted-foreground text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* SUBSCRIBERS */}
        {tab === "subscribers" && (
          <div data-testid="admin-subscribers">
            <h2 className="font-display text-3xl font-light mb-6">Newsletter subscribers ({subscribers.length})</h2>
            {subscribers.length === 0 ? (
              <p className="label text-muted-foreground">No subscribers yet.</p>
            ) : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="label text-muted-foreground text-left">
                      <th className="p-3 border-b border-border font-normal">Email</th>
                      <th className="p-3 border-b border-border font-normal">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((s) => (
                      <tr key={s.id} className="font-mono hover:bg-secondary/50 transition-colors" data-testid={`admin-subscriber-${s.id}`}>
                        <td className="p-3 border-b border-border">{s.email}</td>
                        <td className="p-3 border-b border-border text-muted-foreground text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* AFFILIATES */}
        {tab === "affiliates" && (
          <div data-testid="admin-affiliates">
            <h2 className="font-display text-3xl font-light mb-6">Affiliates ({affiliates.length})</h2>
            {affiliates.length === 0 ? (
              <p className="label text-muted-foreground">No affiliate applications yet.</p>
            ) : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="label text-muted-foreground text-left">
                      <th className="p-3 border-b border-border font-normal">Name</th>
                      <th className="p-3 border-b border-border font-normal">Email</th>
                      <th className="p-3 border-b border-border font-normal">Audience</th>
                      <th className="p-3 border-b border-border font-normal">Status</th>
                      <th className="p-3 border-b border-border font-normal">Promo Code</th>
                      <th className="p-3 border-b border-border font-normal">Referrals</th>
                      <th className="p-3 border-b border-border font-normal">Total Revenue</th>
                      <th className="p-3 border-b border-border font-normal text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliates.map((a) => (
                      <React.Fragment key={a.id}>
                        <tr
                          onClick={() => toggleAffiliateOrders(a.id)}
                          className="font-mono hover:bg-secondary/50 transition-colors cursor-pointer"
                          data-testid={`admin-affiliate-${a.id}`}
                        >
                          <td className="p-3 border-b border-border">
                            <span className="inline-flex items-center gap-2">
                              {expandedAffiliate === a.id ? <ChevronDown size={14} strokeWidth={1.5} className="text-muted-foreground" /> : <ChevronRight size={14} strokeWidth={1.5} className="text-muted-foreground" />}
                              {a.name}
                            </span>
                            <div className="text-xs text-muted-foreground pl-6">{a.company || "—"}</div>
                          </td>
                          <td className="p-3 border-b border-border">{a.email}</td>
                          <td className="p-3 border-b border-border text-xs text-muted-foreground">{a.audience || "—"}</td>
                          <td className="p-3 border-b border-border">
                            <span className={`label px-2 py-1 border ${a.status === "approved" ? "text-accent border-accent" : "text-muted-foreground border-border"}`}>{a.status || "new"}</span>
                          </td>
                          <td className="p-3 border-b border-border tracking-widest">{a.promo_code || "—"}</td>
                          <td className="p-3 border-b border-border">{a.referrals ?? 0}</td>
                          <td className="p-3 border-b border-border" data-testid={`affiliate-revenue-${a.id}`}>${(a.revenue ?? 0).toFixed(2)}</td>
                          <td className="p-3 border-b border-border text-right">
                            {a.status === "approved" ? (
                              <span className="label text-muted-foreground">Approved</span>
                            ) : (
                              <button onClick={(e) => { e.stopPropagation(); approveAffiliate(a.id); }} className="label px-3 py-2 bg-primary text-primary-foreground hover:bg-accent transition-colors" data-testid={`approve-affiliate-${a.id}`}>Approve &amp; generate code</button>
                            )}
                          </td>
                        </tr>
                        {expandedAffiliate === a.id && (
                          <tr data-testid={`affiliate-orders-${a.id}`}>
                            <td colSpan={8} className="p-4 bg-secondary/30 border-b border-border">
                              {!affiliateOrders[a.id] ? (
                                <p className="label text-muted-foreground animate-pulse">Loading orders…</p>
                              ) : affiliateOrders[a.id].length === 0 ? (
                                <p className="label text-muted-foreground">No referred orders yet.</p>
                              ) : (
                                <table className="w-full text-xs border-collapse">
                                  <thead>
                                    <tr className="label text-muted-foreground text-left">
                                      <th className="p-2 font-normal">Order</th>
                                      <th className="p-2 font-normal">Customer</th>
                                      <th className="p-2 font-normal">Date</th>
                                      <th className="p-2 font-normal">Status</th>
                                      <th className="p-2 font-normal text-right">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {affiliateOrders[a.id].map((o) => (
                                      <tr key={o.id} className="font-mono" data-testid={`affiliate-order-${o.id}`}>
                                        <td className="p-2">#{o.order_number}</td>
                                        <td className="p-2">{o.customer}<span className="block text-muted-foreground">{o.email}</span></td>
                                        <td className="p-2 text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                                        <td className="p-2"><span className="label">{o.status.replace(/_/g, " ")}</span></td>
                                        <td className="p-2 text-right">${o.total.toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-4 text-xs text-muted-foreground">Approved affiliates get a unique 10% off promo code. Every order using that code is counted here as a referral.</p>
          </div>
        )}

        {/* BROADCAST */}
        {tab === "broadcast" && (
          <div data-testid="admin-broadcast" className="max-w-4xl">
            <h2 className="font-display text-3xl font-light mb-2">Send a promo email</h2>
            <p className="text-sm text-muted-foreground mb-8">Blast an announcement, promo code, or restock to your list. Sends run in the background — you can leave this page and check the history below.</p>
            <form onSubmit={sendBroadcast} className="space-y-5 max-w-2xl" data-testid="broadcast-form">
              <div>
                <label className="label text-muted-foreground block mb-2">Audience</label>
                <Select value={broadcast.audience} onValueChange={(v) => setBroadcast((b) => ({ ...b, audience: v }))}>
                  <SelectTrigger className="rounded-none border-border font-mono text-xs uppercase tracking-widest h-11" data-testid="broadcast-audience-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="customers" className="rounded-none">Customers only ({customers.length})</SelectItem>
                    <SelectItem value="subscribers" className="rounded-none">Newsletter subscribers only ({subscribers.length})</SelectItem>
                    <SelectItem value="both" className="rounded-none">Everyone (customers + subscribers)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label text-muted-foreground block mb-2">Subject</label>
                <input value={broadcast.subject} onChange={(e) => setBroadcast((b) => ({ ...b, subject: e.target.value }))} required maxLength={140} className={inputCls} data-testid="broadcast-subject" />
              </div>
              <div>
                <label className="label text-muted-foreground block mb-2">Message</label>
                <textarea rows={9} value={broadcast.message} onChange={(e) => setBroadcast((b) => ({ ...b, message: e.target.value }))} required className={`${inputCls} resize-none leading-relaxed`} placeholder="Write your announcement. Blank lines become paragraphs; single line breaks stay as line breaks." data-testid="broadcast-message" />
                <p className="mt-2 text-xs text-muted-foreground">A branded header, Shop the Collection button and disclaimer are added automatically.</p>
              </div>
              <button type="submit" disabled={broadcasting} className="label inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 hover:bg-accent transition-colors disabled:opacity-50" data-testid="broadcast-send">
                {broadcasting ? "Starting…" : "Send broadcast"}
              </button>
            </form>

            <div className="mt-14" data-testid="broadcast-history">
              <h3 className="font-display text-2xl font-light mb-4">Broadcast history</h3>
              {broadcasts.length === 0 ? (
                <p className="label text-muted-foreground">No broadcasts yet.</p>
              ) : (
                <div className="border border-border">
                  {broadcasts.map((b) => (
                    <div key={b.id} className="border-b border-border last:border-b-0" data-testid={`broadcast-job-${b.id}`}>
                      <button onClick={() => toggleBroadcastDetail(b.id)} className="w-full text-left p-4 hover:bg-secondary/50 transition-colors" data-testid={`broadcast-expand-${b.id}`}>
                        <div className="flex justify-between items-center gap-4 flex-wrap">
                          <span className="font-mono text-sm">{b.subject}</span>
                          <span className={`label px-2 py-0.5 border ${b.status === "running" ? "text-accent border-accent animate-pulse" : "text-muted-foreground border-border"}`} data-testid={`broadcast-status-${b.id}`}>
                            {b.status === "running" ? "sending…" : "done"}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-4 text-xs font-mono text-muted-foreground">
                          <span>{b.created_at ? new Date(b.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</span>
                          <span>{AUDIENCE_LABELS[b.audience] || b.audience}</span>
                          <span className="text-accent">sent {b.sent}</span>
                          {b.skipped > 0 && <span>skipped {b.skipped}</span>}
                          {b.failed > 0 && <span className="text-destructive">failed {b.failed}</span>}
                          <span>of {b.total}</span>
                        </div>
                      </button>
                      {openBroadcast === b.id && (
                        <div className="px-4 pb-4" data-testid={`broadcast-detail-${b.id}`}>
                          {!broadcastDetail ? (
                            <p className="label text-muted-foreground animate-pulse">Loading recipients…</p>
                          ) : broadcastDetail.results.length === 0 ? (
                            <p className="label text-muted-foreground">No recipients processed yet.</p>
                          ) : (
                            <div className="max-h-72 overflow-y-auto">
                              <table className="w-full text-xs font-mono">
                                <thead>
                                  <tr className="text-left text-muted-foreground">
                                    <th className="pb-2 pr-3">Email</th>
                                    <th className="pb-2 pr-3">Result</th>
                                    <th className="pb-2 pr-3">Time</th>
                                    <th className="pb-2">Error</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {broadcastDetail.results.map((r, i) => (
                                    <tr key={i} className="border-t border-border" data-testid={`broadcast-recipient-${b.id}-${i}`}>
                                      <td className="py-1.5 pr-3">{r.email}</td>
                                      <td className={`py-1.5 pr-3 ${r.status === "sent" ? "text-accent" : r.status === "failed" ? "text-destructive" : "text-muted-foreground"}`}>{r.status}</td>
                                      <td className="py-1.5 pr-3 text-muted-foreground">{r.at ? new Date(r.at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" }) : "—"}</td>
                                      <td className="py-1.5 text-muted-foreground">{r.error || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* DISCOUNTS */}
        {tab === "discounts" && (
          <div data-testid="admin-discounts">
            <h2 className="font-display text-3xl font-light mb-6">Discount codes</h2>
            <form onSubmit={createDiscount} className="flex flex-wrap gap-3 mb-8 items-end">
              <div>
                <label className="label text-muted-foreground block mb-2">Code</label>
                <input value={newDiscount.code} onChange={(e) => setNewDiscount((d) => ({ ...d, code: e.target.value.toUpperCase() }))} required className={inputCls} data-testid="discount-code-input" />
              </div>
              <div>
                <label className="label text-muted-foreground block mb-2">Type</label>
                <Select value={newDiscount.type} onValueChange={(v) => setNewDiscount((d) => ({ ...d, type: v }))}>
                  <SelectTrigger className="rounded-none border-border font-mono text-xs uppercase tracking-widest h-[38px] w-52" data-testid="discount-type-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="percent" className="rounded-none">Percent off</SelectItem>
                    <SelectItem value="free_shipping" className="rounded-none">Free shipping</SelectItem>
                    <SelectItem value="bundle_half" className="rounded-none">Cheapest item 50% off (3+ items)</SelectItem>
                    <SelectItem value="bogo" className="rounded-none">Buy one get one free (2+ items)</SelectItem>
                    <SelectItem value="fixed" className="rounded-none">Gift card ($ balance)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newDiscount.type === "percent" && (
                <div>
                  <label className="label text-muted-foreground block mb-2">% off</label>
                  <input type="number" min={1} max={100} value={newDiscount.percent_off} onChange={(e) => setNewDiscount((d) => ({ ...d, percent_off: e.target.value }))} required className={`${inputCls} w-24`} data-testid="discount-percent-input" />
                </div>
              )}
              {newDiscount.type === "fixed" && (
                <div>
                  <label className="label text-muted-foreground block mb-2">Gift card value ($)</label>
                  <input type="number" min={1} step="0.01" value={newDiscount.amount} onChange={(e) => setNewDiscount((d) => ({ ...d, amount: e.target.value }))} required className={`${inputCls} w-32`} data-testid="discount-amount-input" />
                </div>
              )}
              <div>
                <label className="label text-muted-foreground block mb-2">Expiry (optional)</label>
                <input type="date" value={newDiscount.expires_at} onChange={(e) => setNewDiscount((d) => ({ ...d, expires_at: e.target.value }))} className={`${inputCls} w-44`} data-testid="discount-expiry-input" />
              </div>
              <button type="submit" className="label inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 h-[38px] hover:bg-accent transition-colors" data-testid="create-discount-btn"><Plus size={16} strokeWidth={1.5} /> Create</button>
            </form>
            <div className="border border-border divide-y divide-border max-w-2xl">
              {discounts.map((d) => (
                <div key={d.id} className="p-4 flex items-center justify-between gap-4 font-mono text-sm" data-testid={`admin-discount-${d.id}`}>
                  <span className="tracking-widest">{d.code}</span>
                  <span className="text-accent">
                    {d.type === "free_shipping" ? "FREE SHIPPING" : d.type === "bundle_half" ? "CHEAPEST 50% OFF (3+)" : d.type === "bogo" ? "BOGO FREE (2+)" : d.type === "fixed" ? `$${d.amount} GIFT CARD` : `${d.percent_off}% OFF`}
                  </span>
                  {d.type === "fixed" && (
                    <span className={`text-xs ${(d.balance ?? d.amount ?? 0) <= 0 ? "text-destructive" : "text-muted-foreground"}`} data-testid={`discount-balance-${d.id}`}>
                      {(d.balance ?? d.amount ?? 0) <= 0 ? "redeemed" : `$${(d.balance ?? d.amount).toFixed(2)} left of $${Number(d.amount).toFixed(2)}`}
                    </span>
                  )}
                  {d.expires_at && (
                    new Date(d.expires_at + "T23:59:59Z") < new Date() ? (
                      <span className="label px-1.5 py-0.5 border border-destructive/50 text-destructive" data-testid={`discount-expired-${d.id}`}>Expired</span>
                    ) : (
                      <span className="text-xs text-muted-foreground" data-testid={`discount-expiry-${d.id}`}>expires {d.expires_at}</span>
                    )
                  )}
                  <span className="text-xs text-muted-foreground" data-testid={`discount-usage-${d.id}`}>used {d.usage_count || 0}×</span>
                  <button onClick={() => deleteDiscount(d.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-discount-${d.id}`}><Trash2 size={16} strokeWidth={1.5} /></button>
                </div>
              ))}
              {discounts.length === 0 && <p className="p-4 label text-muted-foreground">No discount codes.</p>}
            </div>
          </div>
        )}
      </main>

      {/* PRODUCT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-2xl bg-background max-h-[90vh] overflow-y-auto" data-testid="product-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">{editing ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveProduct} className="grid grid-cols-2 gap-4 mt-2">
            <div className="col-span-2">
              <label className="label text-muted-foreground block mb-2">Name</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required className={inputCls} data-testid="product-name-input" />
            </div>
            <div className="col-span-2">
              <label className="label text-muted-foreground block mb-2">Tagline</label>
              <input value={form.tagline} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))} className={inputCls} data-testid="product-tagline-input" />
            </div>
            <div className="col-span-2">
              <label className="label text-muted-foreground block mb-2">Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} data-testid="product-description-input" />
            </div>
            <div>
              <label className="label text-muted-foreground block mb-2">Price ($)</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} required className={inputCls} data-testid="product-price-input" />
            </div>
            <div>
              <label className="label text-muted-foreground block mb-2">Category</label>
              <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} required className={inputCls} data-testid="product-category-input" />
            </div>
            <div>
              <label className="label text-muted-foreground block mb-2">Stock</label>
              <input type="number" value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))} className={inputCls} data-testid="product-stock-input" />
            </div>
            <div className="flex items-end gap-2 pb-2">
              <input id="featured" type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="product-featured-input" />
              <label htmlFor="featured" className="label text-muted-foreground">Featured</label>
            </div>
            <div className="col-span-2">
              <label className="label text-muted-foreground block mb-2">Product image</label>
              <div className="flex items-center gap-4 mb-3">
                {form.image && (
                  <img src={form.image} alt="Preview" className="h-20 w-16 object-cover border border-border bg-secondary" data-testid="product-image-preview" />
                )}
                <label className={`label inline-flex items-center gap-2 border border-border px-4 py-2.5 transition-colors ${uploadingImage ? "opacity-50 cursor-wait" : "cursor-pointer hover:bg-secondary"}`} data-testid="product-image-upload-label">
                  <Upload size={14} strokeWidth={1.5} /> {uploadingImage ? "Uploading…" : "Upload photo"}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleImageUpload} disabled={uploadingImage} data-testid="product-image-upload" />
                </label>
              </div>
              <input value={form.image} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))} className={inputCls} placeholder="/api/media/products/…" data-testid="product-image-input" />
            </div>
            <div className="col-span-2">
              <label className="label text-muted-foreground block mb-2">Ingredients (comma separated)</label>
              <input value={form.ingredients} onChange={(e) => setForm((f) => ({ ...f, ingredients: e.target.value }))} className={inputCls} data-testid="product-ingredients-input" />
            </div>
            <div className="col-span-2">
              <label className="label text-muted-foreground block mb-2">Benefits (comma separated)</label>
              <input value={form.benefits} onChange={(e) => setForm((f) => ({ ...f, benefits: e.target.value }))} className={inputCls} data-testid="product-benefits-input" />
            </div>
            <div className="col-span-2 flex justify-end gap-3 mt-2">
              <button type="button" onClick={() => setDialogOpen(false)} className="label px-5 py-3 border border-border hover:bg-secondary transition-colors" data-testid="product-cancel-btn">Cancel</button>
              <button type="submit" className="label px-5 py-3 bg-primary text-primary-foreground hover:bg-accent transition-colors" data-testid="product-save-btn">{editing ? "Save changes" : "Create product"}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
